// \src\pages\promocoes\page.tsx

import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAdmin } from "@/context/AdminContext";

function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isProductActive(product: {
  active: boolean;
  activeUntil: string;
}): boolean {
  if (!product.active) {
    return false;
  }

  if (!product.activeUntil) {
    return true;
  }

  return product.activeUntil >= getLocalDateString();
}

function isPromotionActive(product: {
  promotionUntil: string;
}): boolean {
  if (!product.promotionUntil) {
    return true;
  }

  return product.promotionUntil >= getLocalDateString();
}

export default function Promocoes() {
  const { data } = useAdmin();
  const products = data.products;
  const categories = data.categories;
  const s = data.settings;
  const promoProducts = products.filter(
    (product) =>
      data.promotions.includes(product.id) &&
      isProductActive(product) &&
      isPromotionActive(product),
  );
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [activeSubcategory, setActiveSubcategory] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const trackRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => { if (trackRef.current) trackRef.current.style.animationPlayState = "paused"; };
  const handleMouseLeave = () => { if (trackRef.current) trackRef.current.style.animationPlayState = "running"; };

  const doubled = [...promoProducts, ...promoProducts];

  const filteredProducts = promoProducts.filter((p) => {
    if (activeSubcategory && p.subcategoryId !== activeSubcategory) return false;
    if (activeCategory && p.category !== activeCategory) return false;

    const query = search.trim().toLowerCase();
    if (!query) return true;

    return [
      p.name,
      p.code,
      p.description,
      p.brand,
      ...(p.specs || []),
    ].some(value => String(value || "").toLowerCase().includes(query));
  });

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50">
        <section className="bg-background-200 border-b border-background-300 py-8 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <nav className="text-xs text-foreground-400 mb-3 font-label">
              <Link to="/" className="hover:text-primary-500 cursor-pointer">Início</Link>
              <span className="mx-2">/</span>
              <span className="text-primary-500">Promoções</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">Promoções</h1>
            <p className="text-foreground-500 text-sm mt-2">As melhores ofertas em materiais de construção</p>
          </div>
        </section>

        {promoProducts.length > 0 && (
          <section className="w-full py-10 bg-background-100 overflow-hidden border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 mb-6">
              <h2 className="text-xl font-heading font-bold text-foreground-950">Produtos em Destaque</h2>
              <p className="text-foreground-500 text-sm">Passe o mouse para pausar · Clique para ver detalhes</p>
            </div>
            <div className="relative w-full overflow-hidden" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              <div ref={trackRef} className="flex gap-5 w-max promo-track animate-scroll-left" style={{ animationDuration: "30s", paddingLeft: "16px" }}>
                {doubled.map((product, i) => (
                  <Link key={`${product.id}-${i}`} to={`/produto/${product.slug}`} className="shrink-0 w-52 bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-all cursor-pointer group">
                    <div className="relative h-40">
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      {product.promoLabel && <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs font-label font-bold px-2 py-0.5 rounded">{product.promoLabel}</span>}
                    </div>
                    <div className="p-3">
                      <p className="text-foreground-900 font-label font-semibold text-xs leading-tight mb-1 line-clamp-2">{product.name}</p>
                      {product.code && <span className="inline-flex items-center bg-primary-50 border border-primary-100 text-primary-700 text-[11px] font-label font-semibold px-2 py-0.5 rounded mt-1">Código: {product.code}</span>}
                      <p className="text-foreground-500 text-xs mt-1">{product.brand}</p>
                      {product.showPrice && <p className="text-primary-600 font-heading font-bold text-base mt-1">R$ {product.price.toFixed(2).replace(".", ",")}</p>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="w-full lg:w-64 shrink-0">
              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden sticky top-24">
                <div className="bg-foreground-950 px-4 py-3">
                  <h3 className="text-background-50 font-heading font-semibold text-sm uppercase tracking-wide">Categorias</h3>
                </div>
                <button onClick={() => { setActiveCategory(null); setActiveSubcategory(null); }} className={`w-full text-left px-4 py-2.5 text-sm font-label transition-colors cursor-pointer border-b border-background-100 ${!activeCategory ? "bg-primary-50 text-primary-600 font-semibold" : "text-foreground-700 hover:bg-background-100"}`}>
                  Todas as Categorias
                </button>
                {categories.map((cat) => (
                  <div key={cat.id}>
                    <button onClick={() => { setActiveCategory(activeCategory === cat.id ? null : cat.id); setActiveSubcategory(null); }} className={`w-full text-left px-4 py-2.5 text-sm font-label transition-colors cursor-pointer flex items-center justify-between border-b border-background-100 ${activeCategory === cat.id ? "bg-primary-50 text-primary-600 font-semibold" : "text-foreground-700 hover:bg-background-100"}`}>
                      <div className="flex items-center gap-2"><i className={`${cat.icon} text-base`} /><span>{cat.name}</span></div>
                      <i className={`text-xs transition-transform ${activeCategory === cat.id ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
                    </button>
                    {activeCategory === cat.id && (
                      <div className="bg-background-100">
                        {cat.subcategories.map((sub) => (
                          <button key={sub.id} onClick={() => setActiveSubcategory(activeSubcategory === sub.id ? null : sub.id)} className={`w-full text-left pl-10 pr-4 py-2 text-xs font-label transition-colors cursor-pointer border-b border-background-200 ${activeSubcategory === sub.id ? "text-primary-600 font-semibold" : "text-foreground-600 hover:text-primary-500"}`}>
                            {sub.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </aside>

            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <h2 className="text-xl font-heading font-bold text-foreground-950">{filteredProducts.length} produto{filteredProducts.length !== 1 ? "s" : ""} encontrado{filteredProducts.length !== 1 ? "s" : ""}</h2>
                <div className="relative w-full sm:w-80">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
                  <input
                    type="search"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Nome, código, descrição, marca ou especificação..."
                    className="w-full pl-9 pr-4 py-2 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.map((product) => (
                  <Link key={product.id} to={`/produto/${product.slug}`} className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer">
                    <div className="relative h-40 overflow-hidden">
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      {product.promoLabel && <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs font-label font-bold px-2 py-0.5 rounded">{product.promoLabel}</span>}
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-foreground-500 font-label mb-1">{product.brand}</p>
                      <p className="text-foreground-900 font-label font-semibold text-sm leading-tight mb-1 line-clamp-2">{product.name}</p>
                      {product.code && <span className="inline-flex items-center bg-primary-50 border border-primary-100 text-primary-700 text-[11px] font-label font-semibold px-2 py-0.5 rounded mt-1">Código: {product.code}</span>}
                      {product.showPrice ? <p className="text-primary-600 font-heading font-bold text-base">R$ {product.price.toFixed(2).replace(".", ",")}</p> : <p className="text-foreground-400 text-xs italic">Consulte o preço</p>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-primary-500 py-14 px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-3">NOSSAS PROMOÇÕES</h2>
          <p className="text-primary-100 text-base mb-6 max-w-md mx-auto">Aproveite as melhores ofertas em materiais de construção. Produtos de qualidade com preços imbatíveis!</p>
          <a href={s.socialWhatsapp} target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 bg-background-50 text-primary-600 hover:bg-background-100 px-8 py-3 rounded-md font-label font-bold text-sm cursor-pointer whitespace-nowrap transition-all">
            <i className="ri-whatsapp-line" /> Quero saber mais
          </a>
        </section>
      </main>
      <Footer />
    </>
  );
}